import { computed, ref } from 'vue'

import { defineStore } from 'pinia'

import { useAiService } from '@/services/ai/useAiService'
import { useAuthStore } from '@/stores/useAuthStore'

type RefreshCreditsOptions = {
  force?: boolean
}

export const useAiCreditsStore = defineStore('ai-credits', () => {
  const aiService = useAiService()
  const authStore = useAuthStore()

  const balanceCredits = ref<number | null>(null)
  const isLoading = ref(false)
  const hasError = ref(false)
  const loadedAuthKey = ref<string | null>(null)

  const currentAuthKey = computed(() => (authStore.isAuthenticated ? 'hub-gateway' : null))

  const hasLoadedBalance = computed(
    () => loadedAuthKey.value !== null && loadedAuthKey.value === currentAuthKey.value,
  )

  const clearCredits = () => {
    balanceCredits.value = null
    loadedAuthKey.value = null
    hasError.value = false
  }

  const refreshCredits = async (options: RefreshCreditsOptions = {}) => {
    const authKey = currentAuthKey.value

    if (!authKey) {
      clearCredits()
      return null
    }

    if (!options.force && hasLoadedBalance.value && balanceCredits.value !== null) {
      return balanceCredits.value
    }

    try {
      isLoading.value = true
      hasError.value = false
      const response = await aiService.getPlatformCredits()
      balanceCredits.value = Number(response.token_balance)
      loadedAuthKey.value = authKey
      return balanceCredits.value
    } catch (error) {
      hasError.value = true
      if (!hasLoadedBalance.value) {
        balanceCredits.value = null
        loadedAuthKey.value = null
      }
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const setCredits = (credits: number) => {
    balanceCredits.value = credits
    loadedAuthKey.value = currentAuthKey.value
    hasError.value = false
  }

  const consumeCredits = (credits: number) => {
    if (balanceCredits.value === null) return
    balanceCredits.value = Math.max(0, balanceCredits.value - credits)
  }

  return {
    balanceCredits,
    isLoading,
    hasError,
    refreshCredits,
    setCredits,
    consumeCredits,
    clearCredits,
  }
})
